import { PLACE } from "./daylight.js";

// MET Norway's terms let a low-volume site call it from the browser, named by its Origin, as long
// as the page credits it (CC BY 4.0).
const SOURCE = "https://api.met.no/weatherapi/locationforecast/2.0/compact"
  + `?lat=${PLACE.latitude.toFixed(4)}&lon=${PLACE.longitude.toFixed(4)}`;
const KEEP_MS = 30 * 60000;
// Rain from `from` mm in the hour, at least `least` of the slider, full at `downpour` mm. A geada at `at` or more of
// three ramps, each [none, full]: the temperature in °C, the cloud cover in %, the wind in m/s.
const RAIN = { from: 0.1, least: 0.45, downpour: 4 };
const GEADA = { cold: [4, 0], clear: [70, 30], still: [6, 3], at: 0.5 };

// The weather at the shop in the hour holding `date`: the tempo knob's setting for it, the
// temperature in °C, and daylight()'s `rain`, `frost` and `cloud`.
export async function forecast(date) {
  const hours = await timeseries();
  const hour = hours.findLast((entry) => Date.parse(entry.time) <= date.getTime()) ?? hours[0];
  return weatherOf(hour.data);
}

// Kept until the Expires MET Norway sends. The browser's cache cannot keep it: its CDN adds an Age
// longer than Expires minus Date, so every response arrives stale and each load asks again.
async function timeseries() {
  const kept = read();
  if (kept && Date.now() < kept.expires) return kept.hours;
  const response = await fetch(SOURCE);
  if (!response.ok) throw new Error(`MET Norway answered ${response.status}`);
  const hours = (await response.json()).properties.timeseries;
  write({ expires: Date.parse(response.headers.get("Expires")) || Date.now() + KEEP_MS, hours });
  return hours;
}

// Storage can be off or full, in a private window say; the page then asks on every load.
function read() {
  try {
    return JSON.parse(localStorage.getItem(SOURCE));
  } catch {
    return null;
  }
}

function write(kept) {
  try {
    localStorage.setItem(SOURCE, JSON.stringify(kept));
  } catch {}
}

// Any rain the hour brings shows, so a drizzle is seen. A geada needs a cold, clear and still night; snow, which the
// diorama cannot show, whitens it the same way.
function weatherOf({ instant: { details }, next_1_hours: next }) {
  const temperature = details.air_temperature;
  const symbol = next?.summary.symbol_code ?? "";
  const millimetres = next?.details.precipitation_amount ?? 0;
  const calm = { temperature, rain: 0, frost: 0, cloud: 0 };
  if (/snow|sleet/.test(symbol)) return { ...calm, tempo: "geada", frost: 1 };
  if (millimetres >= RAIN.from || /rain/.test(symbol)) {
    return { ...calm, tempo: "chuva", rain: RAIN.least + (1 - RAIN.least) * ramp(millimetres, RAIN.from, RAIN.downpour) };
  }
  const frost = ramp(temperature, ...GEADA.cold) * ramp(details.cloud_area_fraction, ...GEADA.clear)
    * ramp(details.wind_speed, ...GEADA.still);
  if (frost >= GEADA.at) return { ...calm, tempo: "geada", frost };
  if (symbol === "cloudy" || symbol === "fog") return { ...calm, tempo: "nuvens", cloud: 1 };
  return { ...calm, tempo: "sol" };
}

function ramp(value, from, to) {
  return Math.min(1, Math.max(0, (value - from) / (to - from)));
}
